import React, { useEffect, useRef, useState } from 'react';
import ClockMovement from './Movement';
import Fence3D from './Fence3D';

const REGISTER = 'https://admin.onshifts.ca/register';
const LOGIN = 'https://admin.onshifts.ca/login';

/* ── Reduced motion ────────────────────────────────── */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/* ── Scroll position (rAF-throttled) ───────────────── */
function useScrollY() {
  const [y, setY] = useState(0);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setY(window.scrollY);
        raf = 0;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return y;
}

/* ── Scroll reveal ─────────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return undefined; }
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Reveal({ children, as: Tag = 'div', className = '', ...rest }) {
  const [ref, inView] = useInView();
  return (
    <Tag ref={ref} className={`reveal ${inView ? 'in' : ''} ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

/* ── Masked line reveal for display headings ───────── */
function MaskTitle({ as: Tag = 'h2', lines, className = '' }) {
  const [ref, inView] = useInView(0.25);
  return (
    <Tag ref={ref} className={`lines ${inView ? 'in' : ''} ${className}`}>
      {lines.map((l, i) => (
        <span key={i} className="line-mask">
          <span className="line" style={{ transitionDelay: `${0.08 + i * 0.11}s` }}>{l}</span>
        </span>
      ))}
    </Tag>
  );
}

/* ── Thin scroll-progress hairline at the very top ── */
function ScrollProgress() {
  const y = useScrollY();
  const [max, setMax] = useState(1);
  useEffect(() => {
    const measure = () =>
      setMax(Math.max(1, document.documentElement.scrollHeight - window.innerHeight));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return (
    <div
      className="progress"
      style={{ transform: `scaleX(${Math.min(1, y / max)})` }}
      aria-hidden="true"
    />
  );
}

/* ── The instrument clock ──────────────────────────────
   Hands follow the real current time; the second hand ticks once per second.
   On load the hands wind forward from 12:00:00 to now, then settle into ticking. */
function useClockAngles() {
  const compute = () => {
    const now = new Date();
    const s = now.getSeconds();
    const m = now.getMinutes();
    const h = now.getHours() % 12;
    return {
      sec: s * 6,
      min: m * 6 + s * 0.1,
      hour: h * 30 + m * 0.5,
    };
  };
  const [angles, setAngles] = useState(compute);
  useEffect(() => {
    const id = setInterval(() => setAngles(compute()), 1000);
    return () => clearInterval(id);
  }, []);
  return angles;
}

const DAY_ABBR = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function InstrumentClock({ reduced }) {
  const angles = useClockAngles();
  const [phase, setPhase] = useState(reduced ? 'run' : 'start');
  useEffect(() => {
    if (reduced) { setPhase('run'); return undefined; }
    let t2;
    const t1 = setTimeout(() => {
      setPhase('wind');
      t2 = setTimeout(() => setPhase('run'), 1750);
    }, 80);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [reduced]);

  const shown = phase === 'start' ? { sec: 0, min: 0, hour: 0 } : angles;
  const hand = (deg) => ({
    transform: `rotate(${deg}deg)`,
    transformOrigin: '300px 300px',
  });

  const now = new Date();
  const dayDate = `${DAY_ABBR[now.getDay()]} ${String(now.getDate()).padStart(2, '0')}`;

  const ticks = [];
  for (let i = 0; i < 60; i++) {
    const major = i % 5 === 0;
    const a = (i * 6 * Math.PI) / 180;
    const r1 = major ? 268 : 278;
    ticks.push(
      <line
        key={i}
        x1={300 + Math.sin(a) * r1} y1={300 - Math.cos(a) * r1}
        x2={300 + Math.sin(a) * 288} y2={300 - Math.cos(a) * 288}
        className={major ? 'clock-tick-major' : 'clock-tick'}
      />
    );
  }

  return (
    <svg className={`clock ${phase === 'wind' ? 'winding' : ''}`} viewBox="0 0 600 600" aria-hidden="true">
      <circle cx="300" cy="300" r="296" className="clock-rim" />
      <circle cx="300" cy="300" r="180" className="clock-inner" />
      {ticks}
      <text x="300" y="64" className="clock-numeral">12</text>
      <text x="536" y="300" className="clock-numeral">3</text>
      <text x="300" y="536" className="clock-numeral">6</text>
      <text x="64" y="300" className="clock-numeral">9</text>
      <text x="300" y="404" className="clock-date">{dayDate}</text>
      <g className="clock-hand-hour" style={hand(shown.hour)}>
        <line x1="300" y1="300" x2="300" y2="170" />
      </g>
      <g className="clock-hand-min" style={hand(shown.min)}>
        <line x1="300" y1="300" x2="300" y2="100" />
      </g>
      <g className="clock-hand-sec" style={hand(shown.sec)}>
        <line x1="300" y1="330" x2="300" y2="88" />
        <circle cx="300" cy="300" r="5" />
      </g>
      <circle cx="300" cy="300" r="3" className="clock-pinion" />
    </svg>
  );
}

/* ── Hour ruler divider ────────────────────────────────
   The page's section grammar: a 0–24 rule whose indigo marker sits at the
   current time of day, with an engraved chapter label at the left. */
function HourRuler({ label }) {
  const now = new Date();
  const pct = ((now.getHours() * 60 + now.getMinutes()) / 1440) * 100;
  return (
    <div className={`ruler ${label ? 'labeled' : ''}`} aria-hidden="true">
      <div className="ruler-ticks" />
      {label && <span className="ruler-tag">{label}</span>}
      {[0, 6, 12, 18, 24].map((h) => (
        <span key={h} className="ruler-label" style={{ left: `calc(28px + (100% - 56px) * ${h / 24})` }}>
          {String(h).padStart(2, '0')}
        </span>
      ))}
      <div className="ruler-now" style={{ left: `calc(28px + (100% - 56px) * ${pct / 100})` }} />
    </div>
  );
}

/* ── Live clock text ───────────────────────────────── */
function LiveTime({ withSeconds = true }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <>
      {now.toLocaleTimeString([], withSeconds
        ? { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }
        : { hour: '2-digit', minute: '2-digit', hour12: false })}
    </>
  );
}

/* ── Side rails: margin companions that ride the scroll ──
   Left: a vertical day-scale — scrolling the page scrubs 00:00 → 24:00,
   a brass marker travels the track, and the current section's name
   crossfades beneath it. Right: a winding gear that turns with the
   scroll and doubles as back-to-top. Desktop margins only. */
const SECTION_LABELS = [
  ['product', 'Live view'],
  ['geofence', 'Geofence'],
  ['workday', 'One shift'],
  ['app', 'The app'],
  ['setup', 'Setup'],
  ['faq', 'Questions'],
];

function SideRails({ reduced }) {
  const y = useScrollY();
  const [max, setMax] = useState(1);
  const [active, setActive] = useState('');

  useEffect(() => {
    const measure = () =>
      setMax(Math.max(1, document.documentElement.scrollHeight - window.innerHeight));
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const hit = SECTION_LABELS.find(([id]) => id === e.target.id);
            if (hit) setActive(hit[1]);
          }
        });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );
    SECTION_LABELS.forEach(([id]) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const p = Math.min(1, y / max);
  const mins = Math.round(p * 288) * 5; // scrub the day in 5-minute steps
  const dayTime = `${String(Math.floor(mins / 60) % 24).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
  const visible = y > 420;

  return (
    <>
      <div className={`rail${visible ? ' on' : ''}`} aria-hidden="true">
        <span className="rail-time">{dayTime}</span>
        <div className="rail-track">
          <span className="rail-dot" style={{ top: `${p * 100}%` }} />
        </div>
        {active && <span key={active} className="rail-section">{active}</span>}
      </div>
      <button
        type="button"
        className={`rail-gear${visible ? ' on' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })}
        aria-label="Back to top"
        tabIndex={visible ? 0 : -1}
      >
        <svg viewBox="0 0 48 48" style={{ transform: reduced ? 'none' : `rotate(${y * 0.12}deg)` }}>
          <circle cx="24" cy="24" r="15" />
          <circle cx="24" cy="24" r="3.5" />
          {Array.from({ length: 10 }, (_, i) => {
            const a = (i / 10) * Math.PI * 2;
            return (
              <line
                key={i}
                x1={24 + Math.sin(a) * 15} y1={24 - Math.cos(a) * 15}
                x2={24 + Math.sin(a) * 21} y2={24 - Math.cos(a) * 21}
              />
            );
          })}
          {[0, 1, 2].map((i) => {
            const a = (i / 3) * Math.PI * 2;
            return (
              <line
                key={`s${i}`}
                x1={24 + Math.sin(a) * 3.5} y1={24 - Math.cos(a) * 3.5}
                x2={24 + Math.sin(a) * 14} y2={24 - Math.cos(a) * 14}
              />
            );
          })}
        </svg>
      </button>
    </>
  );
}

/* ── Pointer tilt: a light 3D lean toward the cursor ── */
function Tilt({ children, max = 8, reduced }) {
  const ref = useRef(null);
  if (reduced) return <div className="tilt">{children}</div>;
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform =
      `perspective(900px) rotateX(${(-y * max).toFixed(2)}deg) rotateY(${(x * max * 1.15).toFixed(2)}deg)`;
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg)';
  };
  return (
    <div ref={ref} className="tilt" onPointerMove={onMove} onPointerLeave={onLeave}>
      {children}
    </div>
  );
}

/* ── Employee app, recreated — and the button works ── */
function PhoneMock() {
  const [punchedAt, setPunchedAt] = useState(null);
  useEffect(() => {
    if (!punchedAt) return undefined;
    const t = setTimeout(() => setPunchedAt(null), 7000);
    return () => clearTimeout(t);
  }, [punchedAt]);
  const hm = punchedAt
    ? punchedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
    : null;
  return (
    <div className="phone">
      <div className="phone-screen">
        <div className="phone-status">
          <span>OnShifts</span>
          <span className="mono"><LiveTime withSeconds={false} /></span>
        </div>
        <div className="phone-greet">Good morning, Sarah</div>
        <div className="phone-shift">
          <div className="phone-shift-label">Today’s shift</div>
          <div className="phone-shift-time">09:00 – 17:00</div>
          <div className="phone-shift-loc">Main St location</div>
        </div>
        <div className="phone-clockin">
          <button
            type="button"
            className={`phone-clockin-btn${punchedAt ? ' done' : ''}`}
            onClick={() => setPunchedAt(new Date())}
            aria-label="Clock in (demo)"
          >
            {punchedAt ? 'Clocked in' : 'Clock in'}
          </button>
        </div>
        <div className="phone-geo">
          <span className="dot ok" />
          {punchedAt ? `On shift since ${hm} — GPS verified` : '38 m from site — inside the geofence'}
        </div>
      </div>
    </div>
  );
}

/* ── "One shift" timeline ──────────────────────────────
   The six capabilities told as timestamped moments of a single workday,
   down a rail that fills with brass as the visitor scrolls through it. */
function useTimelineProgress() {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const anchor = window.innerHeight * 0.6;
      const p = (anchor - rect.top) / rect.height;
      setProgress(Math.max(0, Math.min(1, p)));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);
  return [ref, progress];
}

function TimelineRow({ moment }) {
  const [ref, inView] = useInView(0.35);
  return (
    <div ref={ref} className={`tl-row ${inView ? 'lit' : ''}`}>
      <div className="tl-time">{moment.time}</div>
      <div className="tl-node"><span className="tl-dot" /></div>
      <div className="tl-body">
        <h3 className="tl-title">{moment.title}</h3>
        <p className="tl-desc">{moment.desc}</p>
        <span className="tl-chip" style={{ color: moment.tone }}>{moment.chip}</span>
      </div>
    </div>
  );
}

function DayTimeline({ moments }) {
  const [ref, progress] = useTimelineProgress();
  return (
    <div ref={ref} className="timeline">
      <div className="tl-rail" aria-hidden="true" />
      <div className="tl-fill" style={{ height: `${progress * 100}%` }} aria-hidden="true" />
      {moments.map((m) => <TimelineRow key={m.time} moment={m} />)}
      <div className="tl-end">
        <div className="tl-time">07:58</div>
        <div className="tl-node"><span className="tl-dot end" /></div>
        <div className="tl-body tl-end-note">Tomorrow. It starts again — without you setting anything up.</div>
      </div>
    </div>
  );
}

/* ── The live view: a dashboard that actually behaves live ──
   Roster statuses change on a quiet script, alerts arrive in the feed,
   and the week's hours count themselves up. Demo data, real behaviour. */
const ROSTER_INIT = [
  { name: 'Sarah K.', time: '09:02', status: 'Clocked in', color: 'var(--ok)', tick: 0 },
  { name: 'James R.', time: '10:45', status: 'On break', color: 'var(--warn)', tick: 0 },
  { name: 'Mia T.', time: '08:58', status: 'Clocked out', color: 'var(--bad)', tick: 0 },
  { name: 'Omar N.', time: '09:15', status: 'Clocked in', color: 'var(--ok)', tick: 0 },
];

const ROSTER_SCRIPT = [
  { name: 'James R.', status: 'Clocked in', color: 'var(--ok)' },
  { name: 'Omar N.', status: 'On break', color: 'var(--warn)' },
  { name: 'Mia T.', status: 'Clocked in', color: 'var(--ok)' },
  { name: 'Omar N.', status: 'Clocked in', color: 'var(--ok)' },
  { name: 'Sarah K.', status: 'On break', color: 'var(--warn)' },
  { name: 'Sarah K.', status: 'Clocked in', color: 'var(--ok)' },
];

const ALERTS_INIT = [
  { id: -1, title: 'Late arrival', msg: 'Sarah K. clocked in 12 minutes late.', when: '2m', color: 'var(--bad)' },
  { id: -2, title: 'Clocked in', msg: 'James R. is on shift at Main St.', when: '8m', color: 'var(--ok)' },
  { id: -3, title: 'Shift reminder', msg: 'Evening shift starts in 15 minutes.', when: '14m', color: 'var(--indigo-bright)' },
];

const ALERTS_POOL = [
  { title: 'On break', msg: 'James R. started a 30-minute break.', color: 'var(--warn)' },
  { title: 'Geofence declined', msg: 'A clock-in was tried 1.6 km from Main St.', color: 'var(--bad)' },
  { title: 'Clocked in', msg: 'Omar N. is back on shift.', color: 'var(--ok)' },
  { title: 'Late arrival', msg: 'Mia T. clocked in 4 minutes late.', color: 'var(--bad)' },
  { title: 'Shift reminder', msg: 'Closing shift starts in 30 minutes.', color: 'var(--indigo-bright)' },
];

const WEEK = [
  { name: 'Sarah K.', minutes: 2310 },
  { name: 'Omar N.', minutes: 2475 },
  { name: 'Mia T.', minutes: 2145 },
  { name: 'James R.', minutes: 1320 },
];

function CountUpHours({ minutes, run }) {
  const [m, setM] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (!run || started.current) return undefined;
    started.current = true;
    const t0 = performance.now();
    const dur = 1200;
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setM(Math.round(e * minutes));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [run, minutes]);
  const shown = run || started.current ? m : 0;
  return <>{Math.floor(shown / 60)} h {String(shown % 60).padStart(2, '0')} m</>;
}

function WeekPanel({ reduced }) {
  const [ref, inView] = useInView(0.3);
  const max = Math.max(...WEEK.map((w) => w.minutes));
  const fmt = (mins) => `${Math.floor(mins / 60)} h ${String(mins % 60).padStart(2, '0')} m`;
  return (
    <div ref={ref} className={`panel week-panel ${inView ? 'in' : ''}`}>
      <div className="panel-head">
        <span className="panel-title">This week — timesheet</span>
        <span className="panel-time">auto-logged</span>
      </div>
      {WEEK.map((w, i) => (
        <div key={w.name} className="week-row">
          <span className="week-name">{w.name}</span>
          <span className="bar-track">
            <span
              className="bar-fill"
              style={{ width: `${(w.minutes / max) * 100}%`, transitionDelay: `${0.12 + i * 0.1}s` }}
            />
          </span>
          <span className="week-hours">
            {reduced ? fmt(w.minutes) : <CountUpHours minutes={w.minutes} run={inView} />}
          </span>
        </div>
      ))}
      <p className="week-foot">Every row written by clock-ins and clock-outs, to the minute. Payroll is one export.</p>
    </div>
  );
}

function LiveView({ reduced }) {
  const [ref, inView] = useInView(0.15);
  const [roster, setRoster] = useState(ROSTER_INIT);
  const [alerts, setAlerts] = useState(ALERTS_INIT);
  const step = useRef(0);
  const alertStep = useRef(0);

  useEffect(() => {
    if (reduced || !inView) return undefined;
    const rosterId = setInterval(() => {
      const m = ROSTER_SCRIPT[step.current % ROSTER_SCRIPT.length];
      step.current += 1;
      const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
      setRoster((rs) =>
        rs.map((r) => (r.name === m.name ? { ...r, ...m, time: now, tick: step.current } : r))
      );
    }, 6500);
    const alertsId = setInterval(() => {
      const a = ALERTS_POOL[alertStep.current % ALERTS_POOL.length];
      alertStep.current += 1;
      setAlerts(([x, y]) => [
        { ...a, when: 'now', id: alertStep.current },
        { ...x, when: '6m' },
        { ...y, when: '13m' },
      ]);
    }, 9500);
    return () => { clearInterval(rosterId); clearInterval(alertsId); };
  }, [reduced, inView]);

  return (
    <div ref={ref} className={`live-grid stagger ${inView ? 'in' : ''}`}>
      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">Team status</span>
          <span className="panel-time mono"><span className="live-dot" /><LiveTime /></span>
        </div>
        {roster.map((r) => (
          <div key={`${r.name}-${r.tick}`} className={`roster-row${r.tick ? ' flash' : ''}`}>
            <span className="roster-left">
              <span className="roster-avatar">{r.name.split(' ').map((p) => p[0]).join('')}</span>
              <span className="roster-name">{r.name}</span>
            </span>
            <span className="roster-right">
              <span className="roster-time">{r.time}</span>
              <span className="status" style={{ color: r.color }}>
                <span className="status-dot" style={{ background: r.color }} />
                {r.status}
              </span>
            </span>
          </div>
        ))}
      </div>
      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">Alerts</span>
          <span className="panel-time">today</span>
        </div>
        {alerts.map((a) => (
          <div key={a.id} className={`alert-row${a.when === 'now' ? ' enter' : ''}`}>
            <span className="alert-dot" style={{ background: a.color }} />
            <div>
              <div className="alert-title">{a.title}</div>
              <p className="alert-msg">{a.msg}</p>
            </div>
            <span className={`alert-when${a.when === 'now' ? ' now' : ''}`}>{a.when}</span>
          </div>
        ))}
      </div>
      <WeekPanel reduced={reduced} />
    </div>
  );
}

/* ── FAQ ───────────────────────────────────────────── */
function Faq({ items }) {
  return (
    <div className="faq">
      {items.map((f) => (
        <details key={f.q} className="faq-item">
          <summary className="faq-q">
            {f.q}
            <span className="faq-mark" aria-hidden="true" />
          </summary>
          <p className="faq-a">{f.a}</p>
        </details>
      ))}
    </div>
  );
}

/* ── Footer signature: the time the visitor arrived ── */
function ArrivedAt() {
  const [arrived] = useState(() => new Date());
  return (
    <p className="footer-note mono">
      You’ve been on this page since{' '}
      {arrived.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}.
      We noticed. It’s what we do.
    </p>
  );
}

/* ── Main ──────────────────────────────────────────── */
export default function App() {
  const reduced = usePrefersReducedMotion();
  const y = useScrollY();
  const scrolled = y > 40;

  const day = [
    {
      time: '08:58',
      title: 'Sarah clocks in at the door',
      desc: 'She’s inside the geofence, so the clock-in goes through. From the parking lot across town, it wouldn’t. No buddy-punching, no remote logins.',
      chip: 'GPS verified · 40 m from site',
      tone: 'var(--ok)',
    },
    {
      time: '09:02',
      title: 'Omar is late — you already know',
      desc: 'His shift started at 09:00. Two minutes in, the alert is on your phone. Nobody had to tell you, and nobody could quietly not.',
      chip: 'Alert sent · your phone',
      tone: 'var(--bad)',
    },
    {
      time: '12:30',
      title: 'Meanwhile, at your second location',
      desc: 'Different address, its own geofence, its own staff list — same account. You watch both from one dashboard.',
      chip: 'Location 02 · 6 on shift',
      tone: 'var(--indigo-bright)',
    },
    {
      time: '16:45',
      title: 'Next week’s schedule goes out',
      desc: 'You build it in minutes. The moment you publish, it’s on every employee’s phone — no group chat, no printed sheet on the wall.',
      chip: 'Published · 9 phones updated',
      tone: 'var(--brass)',
    },
    {
      time: '17:04',
      title: 'The timesheet writes itself',
      desc: 'Sarah clocks out and her hours are already logged, to the minute. At the end of the week, payroll is one clean export.',
      chip: 'Entry logged · 8 h 06 m',
      tone: 'var(--ok)',
    },
    {
      time: '17:10',
      title: 'You pardon Omar’s 09:02',
      desc: 'Flat tire — fair enough. One tap from the dashboard and the record shows it was seen, reviewed, and excused.',
      chip: 'Reviewed · 1 tap',
      tone: 'var(--warn)',
    },
  ];

  const appPoints = [
    { k: 'Clock in, clock out', v: 'One tap at the door. The GPS check happens in that moment — accepted or declined on the spot.' },
    { k: 'The schedule, always current', v: 'The week you publish is the week they see. No screenshots of a spreadsheet, no stale printouts.' },
    { k: 'Breaks, logged', v: 'On break and back on shift are one tap each, and the timesheet keeps the minutes honest.' },
  ];

  const faq = [
    {
      q: 'Does OnShifts track my employees all day?',
      a: 'No. Location is checked at the moment someone clocks in or out, against the geofence you set for that location. Between punches, the app isn’t following anyone around.',
    },
    {
      q: 'What stops someone clocking in from home?',
      a: 'The geofence. Each location gets a circle on the map — you choose the radius — and a clock-in only goes through from inside it. From anywhere else, it’s declined and you can see that it was tried.',
    },
    {
      q: 'How do my employees get accounts?',
      a: 'You add them from your dashboard. They download the OnShifts app, sign in with the credentials you created, and their schedule is already there.',
    },
    {
      q: 'Can I run more than one location?',
      a: 'Yes. Every location has its own address, its own geofence, and its own staff list — all under one account, watched from one dashboard.',
    },
    {
      q: 'What happens when the trial ends?',
      a: 'The trial is 5 days, no credit card. When it ends, you subscribe to keep going — your locations, staff, and timesheets stay exactly where you left them.',
    },
    {
      q: 'What if a late clock-in wasn’t their fault?',
      a: 'Pardon it. One tap from the dashboard marks the entry as seen, reviewed, and excused — the record stays honest without anyone getting burned for a flat tire.',
    },
  ];

  // Gentle parallax: text drifts up and fades, the clock lags behind the scroll.
  const py = reduced ? 0 : y;
  const heroFade = reduced ? 1 : Math.max(0, 1 - y / 620);

  return (
    <div>
      <ScrollProgress />
      <SideRails reduced={reduced} />

      {/* ── Nav ── */}
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`}>
        <a href="https://onshifts.ca" className="nav-brand">
          <img src="/onshifts.png" alt="OnShifts" className="nav-logo" />
        </a>
        <div className="nav-links">
          <a href="#product">Live view</a>
          <a href="#geofence">Geofence</a>
          <a href="#workday">One shift</a>
          <a href="#faq">FAQ</a>
        </div>
        <div className="nav-actions">
          <a href={LOGIN} className="btn btn-ghost btn-sm">Log in</a>
          <a href={REGISTER} className="btn btn-primary btn-sm">Start free trial</a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <header className="hero">
        <ClockMovement reduced={reduced} />
        <div className="clock-wrap">
          <div className="clock-par" style={{ transform: `translate3d(0, ${py * 0.12}px, 0)` }}>
            <InstrumentClock reduced={reduced} />
          </div>
        </div>
        <div className="container" style={{ width: '100%' }}>
          <div
            className="hero-inner"
            style={{ transform: `translate3d(0, ${py * 0.18}px, 0)`, opacity: heroFade }}
          >
            <div className="eyebrow rise" style={{ animationDelay: '0.05s' }}>
              GPS-verified time tracking
            </div>
            <MaskTitle
              as="h1"
              className="display hero-title"
              lines={[
                'Run your team',
                <>like <span className="it brass">clockwork.</span></>,
              ]}
            />
            <p className="hero-sub rise" style={{ animationDelay: '0.34s' }}>
              OnShifts gives your team GPS-verified clock-ins, live scheduling,
              and instant alerts, so you always know who's in and who's not.
            </p>
            <div className="hero-ctas rise" style={{ animationDelay: '0.46s' }}>
              <a href={REGISTER} className="btn btn-primary">Start free trial</a>
              <a href="#product" className="btn btn-ghost">See how it works</a>
            </div>
            <div className="hero-fine rise" style={{ animationDelay: '0.56s' }}>
              5-day free trial &middot; no credit card &middot; it’s <LiveTime /> — do you know who’s on shift?
            </div>
          </div>
        </div>
      </header>

      {/* ── Live view ── */}
      <HourRuler label="Live view" />
      <section id="product" className="section">
        <div className="container">
          <div className="section-head">
            <MaskTitle className="display section-title" lines={['Who’s in,', <span key="n" className="it">right now</span>]} />
            <Reveal as="p" className="section-sub">
              The dashboard shows every clock-in as it happens, and the alerts
              come to you — you don't go looking for them.
            </Reveal>
          </div>
          <LiveView reduced={reduced} />
        </div>
      </section>

      {/* ── Geofence ── */}
      <HourRuler label="Geofence" />
      <section id="geofence" className="section">
        <div className="container">
          <div className="geo-grid">
            <div className="geo-copy">
              <MaskTitle
                className="display section-title"
                lines={['Not at the site?', <span key="g" className="it">Not clocked in.</span>]}
              />
              <Reveal as="p" className="section-sub">
                Every location gets a geofence — a circle on the map, sized by
                you. Clock-ins from inside it go through. Clock-ins from a
                parking lot across town don’t, and you can see that they were tried.
              </Reveal>
              <Reveal as="ul" className="geo-facts">
                <li><span className="mono">radius</span>you choose it, per location</li>
                <li><span className="mono">checked</span>at the moment of punch — not all day</li>
                <li><span className="mono">declined</span>logged, visible, impossible to hide</li>
              </Reveal>
            </div>
            <Reveal className="fence-visual">
              <Fence3D reduced={reduced} />
              <div className="fence-chips">
                <span className="fence-chip"><span className="dot ok" />08:58 · 40 m — accepted</span>
                <span className="fence-chip"><span className="dot bad" />09:14 · 1.6 km — declined</span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── One shift, start to finish ── */}
      <HourRuler label="One shift" />
      <section id="workday" className="section">
        <div className="container">
          <div className="section-head">
            <MaskTitle className="display section-title" lines={['A workday, as', <>OnShifts <span className="it">sees it</span></>]} />
            <Reveal as="p" className="section-sub">
              No feature list. Just one ordinary Tuesday, and what the product
              quietly does at each minute of it.
            </Reveal>
          </div>
          <DayTimeline moments={day} />
        </div>
      </section>

      {/* ── The employee app ── */}
      <HourRuler label="The app" />
      <section id="app" className="section">
        <div className="container">
          <div className="app-grid">
            <div className="app-copy">
              <MaskTitle
                className="display section-title"
                lines={['In their pocket,', <>on <span className="it">your screen</span></>]}
              />
              <Reveal as="p" className="section-sub">
                Employees get a deliberately small app: see the schedule, clock
                in at the door, done. You get the other half — the dashboard
                that knows who’s in, who’s late, and who never showed.
              </Reveal>
              <Reveal className="app-points stagger">
                {appPoints.map((p) => (
                  <div key={p.k} className="app-point">
                    <h3 className="app-point-title">{p.k}</h3>
                    <p className="app-point-desc">{p.v}</p>
                  </div>
                ))}
              </Reveal>
            </div>
            <Reveal className="app-visual">
              <Tilt reduced={reduced}>
                <PhoneMock />
              </Tilt>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <HourRuler label="Setup" />
      <section id="setup" className="section">
        <div className="container">
          <div className="section-head">
            <MaskTitle className="display section-title" lines={['Up and running', <>in <span className="it">5 minutes</span></>]} />
          </div>
          <Reveal className="steps-grid stagger">
            <div className="step">
              <div className="step-num">01</div>
              <h3 className="step-title">Register your company</h3>
              <p className="step-desc">
                Create an account and set up your company in under two minutes.
                No credit card required for the free trial.
              </p>
            </div>
            <div className="step">
              <div className="step-num">02</div>
              <h3 className="step-title">Add your team and shifts</h3>
              <p className="step-desc">
                Add employee accounts and build the weekly schedule. Employees
                download the OnShifts app and log straight in.
              </p>
            </div>
            <div className="step">
              <div className="step-num">03</div>
              <h3 className="step-title">Watch it run itself</h3>
              <p className="step-desc">
                Employees clock in from their phones. You get live status,
                alerts, and a clean timesheet without lifting a finger.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ── */}
      <HourRuler label="Questions" />
      <section id="faq" className="section">
        <div className="container">
          <div className="section-head">
            <MaskTitle className="display section-title" lines={['Fair questions,', <span key="f" className="it">straight answers</span>]} />
          </div>
          <Reveal>
            <Faq items={faq} />
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section cta-section">
        <ClockMovement reduced={reduced} variant="cta" />
        <div className="container">
          <Reveal className="cta-panel">
            <div className="cta-fine mono">
              <LiveTime /> &nbsp;·&nbsp; 5-day free trial &nbsp;·&nbsp; no credit card
            </div>
            <h2 className="display cta-title">Ready <span className="it">when you are.</span></h2>
            <p className="cta-sub">Set up your company tonight; see who's on time tomorrow morning.</p>
            <div className="cta-actions">
              <a href={REGISTER} className="btn btn-ink">Start free trial</a>
              <a href={LOGIN} className="btn btn-ink-ghost">Log in</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="footer">
        <div className="container footer-inner">
          <div className="footer-brand">
            <img src="/onshifts.png" alt="OnShifts" style={{ height: 32, objectFit: 'contain' }} />
            <p className="footer-note">&copy; 2026 OnShifts. All rights reserved.</p>
            <ArrivedAt />
          </div>
          <nav className="footer-col" aria-label="Page sections">
            <span className="footer-head">Product</span>
            <a href="#product" className="footer-link">Live view</a>
            <a href="#geofence" className="footer-link">Geofence</a>
            <a href="#workday" className="footer-link">One shift</a>
            <a href="#setup" className="footer-link">Setup</a>
            <a href="#faq" className="footer-link">FAQ</a>
          </nav>
          <nav className="footer-col" aria-label="Account">
            <span className="footer-head">Account</span>
            <a href={REGISTER} className="footer-link">Start free trial</a>
            <a href={LOGIN} className="footer-link">Log in</a>
            <a href={LOGIN} className="footer-link">Admin dashboard</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
